import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { Agency, AgencyKeys } from '@ag-common-lib/lib/models/domain/agency.model';
import {
  AgencyBrandingKeys,
  createAgencyBranding,
  type AgencyBranding,
} from '@ag-common-lib/lib/models/utils/agency-branding.model';
import { HeadshotKeys } from '@ag-common-lib/lib/models/utils/headshot.model';
import {
  AGMedia,
  AGMediaKeys,
  Entity,
  EntityPermissionActivityKeys,
  ImageCropperPayload,
  MediaSize,
} from '@ag-common-lib/public-api';
import { AgMediaUploaderModalComponent } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.component';
import { AgMediaUploaderModalModule } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.module';
import { ColorSketchInputModule } from 'ag-common-svc/lib/components/color-sketch-input/color-sketch-input.module';
import { ImageCropperModalComponent } from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.component';
import {
  revokeCropperObjectUrl,
  waitForCropperImage,
} from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.utils';
import { ImageCropperModalModule } from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.module';
import { ImageWithLoaderModule } from 'ag-common-svc/lib/components/image-with-loader/image-with-loader.module';
import { AgencyImagesService } from 'ag-common-svc/lib/services/agency-images.service';
import { HasPermissionPipe } from 'ag-common-svc/shared/pipes/has-permission.pipe';
import { WasabiImgPipe } from 'ag-common-svc/shared/pipes/wasabi-img.pipe';
import { format } from 'date-fns';
import { confirm } from 'devextreme/ui/dialog';
import { DxButtonModule, DxDropDownButtonModule } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { catchError, from, of, switchMap, tap } from 'rxjs';
import { SharedModule } from '../../../../../shared/shared.module';
import {
  AGENCY_BRANDING_COLOR_FIELDS,
  AGENCY_BRANDING_LOGO_ASPECT_RATIO,
  AGENCY_BRANDING_PREVIEW_PERIOD,
  AGENCY_BRANDING_PREVIEW_ROWS,
  AGENCY_BRANDING_PREVIEW_SUBTITLE,
  AGENCY_BRANDING_PREVIEW_TITLE,
  areAgencyBrandingsEqual,
  getAgencyBrandingPreviewTotals,
} from './agency-branding.model';
import { AgencyBrandingService } from './agency-branding.service';

@Component({
  selector: 'ag-shr-agency-branding',
  standalone: true,
  imports: [
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    ColorSketchInputModule,
    AgMediaUploaderModalModule,
    ImageCropperModalModule,
    ImageWithLoaderModule,
    SharedModule,
    DxButtonModule,
    DxDropDownButtonModule,
  ],
  providers: [AgencyBrandingService, HasPermissionPipe, WasabiImgPipe],
  templateUrl: './agency-branding.component.html',
  styleUrl: './agency-branding.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'agency-branding' },
})
export class AgencyBrandingComponent {
  @ViewChild(AgMediaUploaderModalComponent, { static: false })
  private mediaUploaderComponent?: AgMediaUploaderModalComponent;
  @ViewChild(ImageCropperModalComponent, { static: false })
  private imageCropperModalComponent?: ImageCropperModalComponent;

  private readonly agencyBrandingService = inject(AgencyBrandingService);
  private readonly hasPermissionPipe = inject(HasPermissionPipe);
  private readonly wasabiImgPipe = inject(WasabiImgPipe);
  private readonly agencyImagesService = inject(AgencyImagesService);
  private readonly toastrService = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  readonly agency = input.required<Agency>();
  readonly brandingChange = output<AgencyBranding>();

  protected readonly inEditModeSig = signal(false);
  protected readonly isPreparingLogoCropperSig = signal(false);
  private logoCropperObjectUrl: string | null = null;
  protected readonly draftBrandingSig = signal<Required<AgencyBranding> | null>(null);
  private readonly brandingOverrideSig = signal<Required<AgencyBranding> | null>(null);
  protected readonly generatedAtLabel = format(new Date(), "EEEE, MMMM d, yyyy 'at' h:mm a");

  private readonly fetchedBrandingSig = toSignal(
    toObservable(computed(() => this.agency()[AgencyKeys.agencyId] ?? null)).pipe(
      switchMap(agencyId => {
        this.brandingOverrideSig.set(null);
        this.draftBrandingSig.set(null);
        this.inEditModeSig.set(false);

        if (!agencyId) {
          return of(createAgencyBranding(null));
        }

        return from(this.agencyBrandingService.getByAgencyId(agencyId)).pipe(
          tap(branding => {
            this.agency()[AgencyKeys.branding] = branding;
            this.brandingChange.emit(branding);
          }),
          catchError(() => of(createAgencyBranding(this.agency()[AgencyKeys.branding]))),
        );
      }),
    ),
    { initialValue: createAgencyBranding(null) },
  );

  protected readonly canEditSig = toSignal(
    this.hasPermissionPipe.transform(Entity.agencyBranding, EntityPermissionActivityKeys.write),
    { initialValue: false },
  );

  protected readonly canCreateSig = toSignal(
    this.hasPermissionPipe.transform(Entity.agencyBranding, EntityPermissionActivityKeys.create),
    { initialValue: false },
  );

  private readonly savedBrandingSig = computed(() => {
    const brandingOverride = this.brandingOverrideSig();
    if (brandingOverride) {
      return brandingOverride;
    }

    return this.fetchedBrandingSig();
  });

  protected readonly displayBrandingSig = computed(() => {
    const draftBranding = this.draftBrandingSig();
    if (this.inEditModeSig() && draftBranding) {
      return draftBranding;
    }

    return this.savedBrandingSig();
  });

  protected readonly previewStyleSig = computed(() => {
    const branding = this.displayBrandingSig();
    return {
      '--agency-branding-banner': branding[AgencyBrandingKeys.bannerColor],
      '--agency-branding-bulletin-title': branding[AgencyBrandingKeys.bulletinTitleColor],
      '--agency-branding-report-title': branding[AgencyBrandingKeys.reportTitleColor],
      '--agency-branding-table-text': branding[AgencyBrandingKeys.tableTextColor],
    };
  });

  protected readonly previewLogoSig = computed(() => {
    const wasabiPath = this.displayBrandingSig()[AgencyBrandingKeys.logo]?.[HeadshotKeys.wasabiPath]?.trim();
    return wasabiPath || null;
  });

  protected readonly logoCropperPayloadSig = computed(
    () => this.displayBrandingSig()[AgencyBrandingKeys.logo]?.[HeadshotKeys.imageCropperPayload] ?? null,
  );

  protected readonly mediaPathPrefixSig = computed(() => {
    const agencyDbId = this.agency()[BaseModelKeys.dbId];
    return agencyDbId ? `agencies/${agencyDbId}` : '';
  });

  protected readonly agencyMediaDataSource$ = toObservable(
    computed(() => this.agency()[BaseModelKeys.dbId] ?? null),
  ).pipe(switchMap(agencyDbId => (agencyDbId ? this.agencyImagesService.fetchAgencyImages(agencyDbId) : of([]))));

  protected readonly previewTotals = getAgencyBrandingPreviewTotals(AGENCY_BRANDING_PREVIEW_ROWS);

  protected readonly AgencyKeys = AgencyKeys;
  protected readonly AgencyBrandingKeys = AgencyBrandingKeys;
  protected readonly MediaSize = MediaSize;
  protected readonly colorFields = AGENCY_BRANDING_COLOR_FIELDS;
  protected readonly logoAspectRatio = AGENCY_BRANDING_LOGO_ASPECT_RATIO;
  protected readonly previewRows = AGENCY_BRANDING_PREVIEW_ROWS;
  protected readonly previewTitle = AGENCY_BRANDING_PREVIEW_TITLE;
  protected readonly previewSubtitle = AGENCY_BRANDING_PREVIEW_SUBTITLE;
  protected readonly previewPeriod = AGENCY_BRANDING_PREVIEW_PERIOD;

  constructor() {
    this.destroyRef.onDestroy(() => {
      revokeCropperObjectUrl(this.logoCropperObjectUrl);
    });
  }

  protected onEdit(): void {
    this.draftBrandingSig.set(createAgencyBranding(this.savedBrandingSig()));
    this.inEditModeSig.set(true);
  }

  protected async onRevert(): Promise<void> {
    if (this.hasDraftChanges()) {
      const isConfirmed = await confirm('<i>Are you sure you want to Cancel without Saving?</i>', 'Confirm');
      if (!isConfirmed) {
        return;
      }
    }

    this.draftBrandingSig.set(null);
    this.inEditModeSig.set(false);
  }

  protected async onSave(): Promise<void> {
    const agencyId = this.agency()[AgencyKeys.agencyId];
    if (!agencyId) {
      return;
    }

    const branding = this.agencyBrandingService.normalize(
      this.draftBrandingSig() ?? this.displayBrandingSig(),
    );

    const savedBranding = await this.agencyBrandingService.updateByAgencyId(agencyId, branding, {
      showSuccessToast: true,
    });
    this.applyPersistedBranding(savedBranding);
    this.draftBrandingSig.set(null);
    this.inEditModeSig.set(false);
  }

  protected onColorChange(key: AgencyBrandingKeys, value: string): void {
    this.patchDraft({ [key]: value });
  }

  protected onUploadClicked = (): void => {
    this.mediaUploaderComponent?.showModal();
  };

  protected handleCropImage = (): void => {
    const logo = this.previewLogoSig();
    if (!logo) {
      return;
    }

    this.imageCropperModalComponent?.showModal(
      this.wasabiImgPipe.transform(logo),
      this.logoCropperPayloadSig() ?? undefined,
    );
  };

  protected onImageCropped = async (payload: ImageCropperPayload): Promise<void> => {
    const nextBranding = this.agencyBrandingService.applyImageCropped(this.displayBrandingSig(), payload);
    this.patchDraft({ [AgencyBrandingKeys.logo]: nextBranding[AgencyBrandingKeys.logo] });
  };

  protected onMediaChanged = async (mediaUrl: AGMedia): Promise<void> => {
    const agencyDbId = this.agency()[BaseModelKeys.dbId];
    const wasabiPath = mediaUrl?.[AGMediaKeys.wasabiPath];
    if (!agencyDbId || !wasabiPath) {
      return;
    }

    this.isPreparingLogoCropperSig.set(true);

    try {
      const nextBranding = await this.agencyBrandingService.applyLogoImageChanged(
        agencyDbId,
        this.displayBrandingSig(),
        mediaUrl,
      );
      this.patchDraft({ [AgencyBrandingKeys.logo]: nextBranding[AgencyBrandingKeys.logo] });

      const pictureUrl = this.buildCropperPictureUrl(wasabiPath);
      const cropperImageUrl = await waitForCropperImage(pictureUrl);
      if (!cropperImageUrl) {
        this.toastrService.error('Unable to load the uploaded image. Please try Edit Branding Logo again.');
        return;
      }

      revokeCropperObjectUrl(this.logoCropperObjectUrl);
      this.logoCropperObjectUrl = cropperImageUrl;
      this.imageCropperModalComponent?.showModal(cropperImageUrl);
    } finally {
      this.isPreparingLogoCropperSig.set(false);
    }
  };

  private buildCropperPictureUrl(wasabiPath: string): string {
    return this.wasabiImgPipe.transform(wasabiPath) ?? '';
  }

  private applyPersistedBranding(branding: Required<AgencyBranding>): void {
    this.brandingOverrideSig.set(branding);
    this.agency()[AgencyKeys.branding] = branding;
    this.brandingChange.emit(branding);
  }

  private patchDraft(patch: Partial<AgencyBranding>): void {
    this.draftBrandingSig.update(draftBranding => ({
      ...(draftBranding ?? createAgencyBranding(this.savedBrandingSig())),
      ...patch,
    }));
  }

  private hasDraftChanges(): boolean {
    const draftBranding = this.draftBrandingSig();
    if (!draftBranding) {
      return false;
    }

    return !areAgencyBrandingsEqual(draftBranding, createAgencyBranding(this.savedBrandingSig()));
  }
}
